/*
 * PZ product-page behaviours: delivery estimate, sticky add-to-cart, add-ons.
 *
 * Loaded by main-product.liquid with `defer`, after Dawn's pubsub.js and
 * global.js, so `subscribe`, `PUB_SUB_EVENTS`, `fetchConfig` and `routes` are
 * globals here exactly as they are in Dawn's own product-form.js.
 *
 * Nothing in this file touches the add-to-cart request itself. The sticky bar
 * clicks the REAL submit button, so both personalizer apps keep intercepting
 * the one form the theme owns (the GBA widget validates + saves a session on
 * submit; Customily gates the button until it has loaded).
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Delivery estimate                                                   */
  /* ------------------------------------------------------------------ */

  const DAY_MS = 24 * 60 * 60 * 1000;

  function addBusinessDays(start, days, skipWeekends) {
    const date = new Date(start.getTime());
    let remaining = days;
    while (remaining > 0) {
      date.setTime(date.getTime() + DAY_MS);
      const dow = date.getDay();
      if (skipWeekends && (dow === 0 || dow === 6)) continue;
      remaining -= 1;
    }
    return date;
  }

  function addDays(start, days) {
    return new Date(start.getTime() + days * DAY_MS);
  }

  function formatDate(date, locale) {
    try {
      return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date);
    } catch (e) {
      return date.toDateString().slice(4, 10);
    }
  }

  function formatRange(from, to, locale) {
    const a = formatDate(from, locale);
    const b = formatDate(to, locale);
    return a === b ? a : `${a} – ${b}`;
  }

  class PzDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.locale = document.documentElement.lang || 'en';
      this.method = this.dataset.defaultMethod === 'express' && this.hasExpress() ? 'express' : 'standard';
      this.querySelectorAll('[data-pz-method]').forEach((pill) => {
        pill.addEventListener('click', () => {
          this.method = pill.dataset.pzMethod;
          this.render();
        });
      });
      this.render();
    }

    hasExpress() {
      return this.dataset.expressMin !== undefined && this.dataset.expressMin !== '';
    }

    int(name, fallback) {
      const value = parseInt(this.dataset[name], 10);
      return Number.isFinite(value) ? value : fallback;
    }

    // "Today" in the shop's own clock, so the cut-off hour means the same thing
    // for every shopper. tzOffsetMin is the shop's UTC offset at render time.
    shopNow() {
      const now = new Date();
      const offset = this.int('tzOffsetMin', -now.getTimezoneOffset());
      const utc = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
      return new Date(utc + offset * 60 * 1000);
    }

    compute() {
      const skipWeekends = this.dataset.skipWeekends === 'true';
      const cutoff = this.int('cutoffHour', 24);
      let start = this.shopNow();
      if (start.getHours() >= cutoff) start = addDays(start, 1);
      // Never promise a start on a weekend when production skips them.
      if (skipWeekends) {
        while (start.getDay() === 0 || start.getDay() === 6) start = addDays(start, 1);
      }
      const shipsFrom = addBusinessDays(start, this.int('prodMin', 1), skipWeekends);
      const shipsTo = addBusinessDays(start, this.int('prodMax', 3), skipWeekends);
      const express = this.method === 'express';
      const shipMin = this.int(express ? 'expressMin' : 'shipMin', 5);
      const shipMax = this.int(express ? 'expressMax' : 'shipMax', 10);
      return {
        placed: start,
        shipsFrom,
        shipsTo,
        deliveredFrom: addDays(shipsFrom, shipMin),
        deliveredTo: addDays(shipsTo, shipMax),
      };
    }

    render() {
      const d = this.compute();
      const set = (selector, text) => {
        this.querySelectorAll(selector).forEach((el) => (el.textContent = text));
      };
      set('[data-pz-earliest]', formatDate(d.deliveredFrom, this.locale));
      set('[data-pz-window]', formatRange(d.deliveredFrom, d.deliveredTo, this.locale));
      set('[data-pz-placed]', formatDate(d.placed, this.locale));
      set('[data-pz-ships]', formatRange(d.shipsFrom, d.shipsTo, this.locale));
      set('[data-pz-delivered]', formatRange(d.deliveredFrom, d.deliveredTo, this.locale));
      this.querySelectorAll('[data-pz-method]').forEach((pill) => {
        pill.setAttribute('aria-pressed', pill.dataset.pzMethod === this.method ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('pz-delivery-estimate')) {
    customElements.define('pz-delivery-estimate', PzDeliveryEstimate);
  }

  /* ------------------------------------------------------------------ */
  /* Sticky add to cart                                                  */
  /* ------------------------------------------------------------------ */

  class PzStickyAtc extends HTMLElement {
    /**
     * The button this bar mirrors. Normally Dawn's own submit button, but a
     * personalizer app may hide it and mount its own in the same form —
     * Customily replaces #ProductSubmitButton-… (display:none) with
     * #customily-cart-btn. Mirroring a display:none button would mean a
     * zero-size rect (the bar never shows) and a click that bypasses the
     * app's validation, so always prefer a VISIBLE submit button in the
     * product form.
     */
    resolveTarget() {
      const visible = (el) => !!el && el.offsetParent !== null && el.getBoundingClientRect().height > 0;
      const byId = document.getElementById(this.dataset.target);
      if (visible(byId)) return byId;
      // The bar is rendered after </product-info>, so walk in from the
      // button it was given rather than up from itself.
      const scope = (byId && byId.closest('product-info')) || document.querySelector('product-info') || document;
      const candidates = scope.querySelectorAll('.product-form__submit');
      for (const candidate of candidates) {
        if (visible(candidate)) return candidate;
      }
      return byId;
    }

    connectedCallback() {
      this.target = this.resolveTarget();
      this.button = this.querySelector('[data-pz-sticky-submit]');
      if (!this.target || !this.button) return;

      this.button.addEventListener('click', () => {
        // The real button may be disabled (sold out, Customily still loading):
        // clicking it then is a no-op, which is the right answer here too.
        if (this.target.disabled || this.target.getAttribute('aria-disabled') === 'true') {
          this.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.target.click();
        // The widget answers a blocked submit (still loading, failed save)
        // with an inline message next to the form, which a shopper at the
        // bottom of the page cannot see. If nothing opened, bring it into view.
        window.setTimeout(() => {
          const opened = document.querySelector('cart-drawer.active, cart-notification.active, dialog[open]');
          if (!opened) this.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 900);
      });

      // Show once the main button has scrolled ABOVE the viewport — not while
      // it is still below the fold on a tall gallery, where a sticky bar would
      // just cover content the shopper is about to reach. Driven by scroll +
      // resize rather than an IntersectionObserver: a fling or anchor jump can
      // move the button from below the fold to above it without ever
      // intersecting, and an observer that only fires on crossings then never
      // shows the bar.
      // Leading-edge throttle with a trailing catch-up: the first scroll event
      // updates synchronously (timers are throttled in background tabs), and
      // one deferred pass settles the final position.
      this.onScroll = () => {
        const now = Date.now();
        if (now - (this.lastVisibilityRun || 0) >= 40) {
          this.lastVisibilityRun = now;
          this.updateVisibility();
          return;
        }
        if (this.trailingTimer) return;
        this.trailingTimer = window.setTimeout(() => {
          this.trailingTimer = null;
          this.lastVisibilityRun = Date.now();
          this.updateVisibility();
        }, 80);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.updateVisibility();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.syncVariant(event));
      }
      this.syncButtonState();
      this.stateObserver = new MutationObserver(() => this.syncButtonState());
      this.stateObserver.observe(this.target, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.trailingTimer) window.clearTimeout(this.trailingTimer);
      document.body.classList.remove('pz-sticky-visible');
      this.stateObserver?.disconnect();
      this.unsubscribe?.();
    }

    updateVisibility() {
      // Re-resolve every pass: Dawn's section rendering replaces the
      // buy-buttons node, and a personalizer app swaps the visible button in
      // after its own scripts load.
      const target = this.resolveTarget() || this.target;
      if (!target) return;
      if (target !== this.target) {
        this.target = target;
        this.stateObserver?.disconnect();
        this.stateObserver?.observe(target, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
        this.syncButtonState();
      }
      const rect = target.getBoundingClientRect();
      const visible = rect.bottom < 0;
      this.classList.toggle('is-visible', visible);
      // Reserve the bar's height at the page end so it never covers the
      // footer's last links (padding lives on body via pz-theme.css).
      document.body.classList.toggle('pz-sticky-visible', visible);
      if (visible) {
        document.documentElement.style.setProperty('--pz-sticky-h', `${this.offsetHeight}px`);
      }
    }

    syncButtonState() {
      const disabled = this.target.disabled || this.target.getAttribute('aria-disabled') === 'true';
      this.button.classList.toggle('button--secondary', disabled);
      const label = this.target.querySelector('span');
      const own = this.button.querySelector('span');
      if (label && own && disabled) own.textContent = label.textContent.trim();
      if (own && !disabled) own.textContent = this.dataset.label || own.textContent;
    }

    syncVariant(event) {
      const html = event?.data?.html;
      const sectionId = event?.data?.sectionId;
      if (!html || !sectionId) return;
      const nextPrice = html.getElementById(`price-${sectionId}`);
      const ownPrice = this.querySelector('[data-pz-sticky-price]');
      if (nextPrice && ownPrice) ownPrice.innerHTML = nextPrice.innerHTML;
      const variant = event.data.variant;
      const media = this.querySelector('[data-pz-sticky-media] img');
      const src = variant?.featured_image?.src;
      if (media && src) {
        // The variant JSON carries the unsized original upload; ask the CDN
        // for thumbnail sizes instead of loading a 3000px file into 56px.
        try {
          const one = new URL(src, window.location.href);
          const two = new URL(src, window.location.href);
          one.searchParams.set('width', '120');
          two.searchParams.set('width', '240');
          media.src = one.href;
          media.srcset = `${one.href} 1x, ${two.href} 2x`;
        } catch (e) {
          media.src = src;
        }
      }
    }
  }

  if (!customElements.get('pz-sticky-atc')) {
    customElements.define('pz-sticky-atc', PzStickyAtc);
  }

  /* ------------------------------------------------------------------ */
  /* Gallery mirror — the widget's preview on the product image          */
  /* ------------------------------------------------------------------ */

  /*
   * The GBA widget draws its live preview on a canvas inside its own card.
   * Macorner (and shoppers) expect the personalization to show up on the
   * main product image as well, so mirror that canvas onto the gallery's
   * active image. The widget replaces its canvas element on every render
   * (preview_host.replaceChildren), which a childList observer sees; a short
   * trailing timer coalesces keystroke-by-keystroke renders before the
   * comparatively expensive toDataURL.
   *
   * Nothing here talks to the widget's API; it only reads a canvas that is
   * already on screen. If the widget is absent, nothing runs.
   */
  class PzGalleryMirror {
    constructor(root) {
      this.root = root;
      this.section = root.closest('product-info');
      this.timer = null;
      this.items = new Set();
      this.openPreview = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const button = this.section && this.section.querySelector('.gbapz__preview-button');
        if (event.type === 'click' && button && !button.disabled) button.click();
      };
      // Watch the whole widget root, not the preview host: the widget builds
      // that host after its bundle loads and rebuilds it on a variant change,
      // so an observer pinned to one host node would go stale.
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(root, { childList: true, subtree: true });
      this.schedule();
    }

    schedule() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.paint();
      }, 250);
    }

    // Always the FIRST gallery image (the product mockup), never whichever
    // one happens to be active: a shopper looking at the size chart while
    // typing a name must not have that chart painted over.
    targetImage() {
      const gallery = this.section && this.section.querySelector('media-gallery');
      return gallery ? gallery.querySelector('.product__media-item img') : null;
    }

    paint() {
      const canvas = this.root.querySelector('.gbapz__preview canvas');
      const img = this.targetImage();
      if (!canvas || !img || !canvas.width || !canvas.height) return;
      let url;
      try {
        url = canvas.toDataURL('image/png');
      } catch (e) {
        return; // a tainted canvas (cross-origin asset) cannot be read; keep the photo
      }
      if (!img.dataset.pzOriginalSrc) {
        img.dataset.pzOriginalSrc = img.currentSrc || img.src;
        img.dataset.pzOriginalSrcset = img.srcset || '';
        img.dataset.pzOriginalSizes = img.sizes || '';
      }
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
      img.classList.add('pz-mirrored');
      // The matching thumbnail, so the strip agrees with the main image.
      const item = img.closest('.product__media-item');
      if (item && !this.items.has(item)) {
        this.items.add(item);
        item.classList.add('pz-personalized-media');
        item.addEventListener('click', this.openPreview, true);
        item.addEventListener('dblclick', this.openPreview, true);
        const toggle = item.querySelector('.product__media-toggle');
        if (toggle) toggle.setAttribute('aria-label', 'Preview your personalization');
      }
      const mediaId = item && item.dataset.mediaId;
      if (mediaId) {
        const thumb = this.section.querySelector(`.thumbnail[data-target="${mediaId}"] img`);
        if (thumb) {
          thumb.removeAttribute('srcset');
          thumb.src = url;
        }
      }
    }

    destroy() {
      this.observer.disconnect();
      if (this.timer) window.clearTimeout(this.timer);
      for (const item of this.items) {
        item.removeEventListener('click', this.openPreview, true);
        item.removeEventListener('dblclick', this.openPreview, true);
      }
      this.items.clear();
    }
  }

  function mountGalleryMirror() {
    const root = document.querySelector('[data-gbapz-root]');
    if (!root) return;
    if (root.pzMirror) {
      root.pzMirror.schedule();
      return;
    }
    root.pzMirror = new PzGalleryMirror(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountGalleryMirror, { once: true });
  } else {
    mountGalleryMirror();
  }
  // The theme editor re-renders the section (fresh widget root, fresh gallery
  // image) on every setting change; the widget bundle re-mounts itself, so
  // the mirror has to follow or the editor preview stays on the stock photo.
  document.addEventListener('shopify:section:load', mountGalleryMirror);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const root of event.target.querySelectorAll('[data-gbapz-root]')) {
      if (root.pzMirror) {
        root.pzMirror.destroy();
        delete root.pzMirror;
      }
    }
  });

  /* ------------------------------------------------------------------ */
  /* Bottom-right overlay (chat launcher)                                */
  /* ------------------------------------------------------------------ */

  /*
   * Shopify Inbox and every other chat app park a round launcher ~20px from
   * the bottom-right corner — exactly over the sticky bar's Add to cart
   * button and over the right end of the widget's own sticky action bar.
   * Rather than hard-coding one app's markup, hit-test the corner and let
   * CSS reserve room through --pz-overlay-w. Chat scripts load late, so the
   * probe runs again a few seconds in, and on resize.
   */
  // null = our own bar is on top there, so the answer is unknown.
  function overlayAt(x, y) {
    const node = document.elementFromPoint(x, y);
    if (!node || node.closest('pz-sticky-atc')) return null;
    let el = node;
    while (el && el !== document.body && el !== document.documentElement) {
      if (window.getComputedStyle(el).position === 'fixed') return true;
      el = el.parentElement;
    }
    return false;
  }

  function probeBottomOverlay() {
    if (!document.body) return;
    const y = window.innerHeight - 24;
    const w = window.innerWidth;
    if (overlayAt(w - 24, y) !== true) {
      document.documentElement.style.setProperty('--pz-overlay-w', '0px');
      return;
    }
    // Walk left in small steps until the corner is clear again: that width
    // is what the launcher actually occupies, whichever app draws it. An
    // overlay still covering the bottom edge 208px in is not a launcher but
    // a full-width bar (cookie banner, the theme preview bar) — nothing to
    // dodge there, so reserve nothing.
    let reserve = 0;
    for (let x = 48; x <= 208; x += 16) {
      const hit = overlayAt(w - x, y);
      if (hit === null) return;
      if (hit === false) {
        reserve = x;
        break;
      }
    }
    document.documentElement.style.setProperty('--pz-overlay-w', reserve ? `${reserve}px` : '0px');
  }

  function scheduleOverlayProbe() {
    probeBottomOverlay();
    window.setTimeout(probeBottomOverlay, 2000);
    window.setTimeout(probeBottomOverlay, 5000);
  }

  if (document.readyState === 'complete') {
    scheduleOverlayProbe();
  } else {
    window.addEventListener('load', scheduleOverlayProbe, { once: true });
  }
  window.addEventListener('resize', probeBottomOverlay, { passive: true });

  /* ------------------------------------------------------------------ */
  /* Add-ons — "complete the gift"                                       */
  /* ------------------------------------------------------------------ */

  function cartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  // Dawn's cart page owns its own line list and footer; after an add there a
  // reload is the simplest correct thing (its CartItems only re-renders after
  // quantity changes it made itself).
  function onCartPage() {
    return !!document.querySelector('cart-items');
  }

  function showCartError(message) {
    const el = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (el) el.textContent = message || '';
  }

  function addItems(items) {
    const cart = onCartPage() ? null : cartElement();
    const config = fetchConfig('javascript');
    const body = { items };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }
    config.body = JSON.stringify(body);
    return fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || 'cart error');
        showCartError('');
        if (!cart) {
          window.location.reload();
          return response;
        }
        cart.classList.remove('is-empty');
        // A multi-item add answers `{ items: [...] }` with no top-level key or
        // id; cart-notification needs the line key to find its product section.
        const last = Array.isArray(response.items) ? response.items[response.items.length - 1] : null;
        cart.renderContents(last && !response.key ? { ...last, ...response } : response);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pz-addons', cartData: response });
        }
        return response;
      })
      .catch((error) => {
        showCartError(error.message || window.cartStrings?.error || '');
        throw error;
      });
  }

  class PzAddons extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.querySelectorAll('[data-pz-addon-add]').forEach((button) => {
        button.addEventListener('click', () => {
          const id = button.dataset.pzAddonAdd;
          button.classList.add('is-loading');
          button.setAttribute('aria-disabled', 'true');
          addItems([{ id, quantity: 1 }])
            .then(() => {
              button.textContent = button.dataset.addedLabel || button.textContent;
            })
            .catch(() => {})
            .finally(() => {
              button.classList.remove('is-loading');
              button.removeAttribute('aria-disabled');
            });
        });
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    checkedVariantIds() {
      return [...this.querySelectorAll('input[data-pz-addon-variant]:checked')].map(
        (input) => input.dataset.pzAddonVariant,
      );
    }

    onCartUpdate(event) {
      // Only ride along with THIS product's own add-to-cart, never with a
      // quick-add of something else on the page.
      if (event?.source !== 'product-form') return;
      const productId = String(event?.cartData?.product_id || '');
      if (!productId || productId !== String(this.productId)) return;
      const ids = this.checkedVariantIds();
      if (ids.length === 0) return;
      return addItems(ids.map((id) => ({ id, quantity: 1 })))
        .then(() => {
          this.querySelectorAll('input[data-pz-addon-variant]:checked').forEach((input) => (input.checked = false));
        })
        .catch((e) => console.warn('[pz-addons]', e));
    }
  }

  if (!customElements.get('pz-addons')) {
    customElements.define('pz-addons', PzAddons);
  }
})();
